import type { ReactNode } from 'react';

import { Link } from 'react-router';

import { cn } from '@rocco/utils/cn';
import { ARTICLE_DISPLAY_TYPE } from '~/modules/article-revamp/util/article-display-type';

/**
 * The type every section title on a detail page is set in: the sans face at
 * the page's display step.
 *
 * Exported because a few sections draw their own heading rather than taking
 * this component — they read it from here so one section cannot end up a step
 * off the ones above and below it.
 */
export const SECTION_TITLE_TYPE =
    'font-sans leading-[1.05] font-medium max-md:text-[2rem] max-md:tracking-[-0.03em] md:text-[4.5rem] md:tracking-[-0.032em]';

/**
 * The page's section head (Figma 149:13425): a small tracked eyebrow carrying
 * the section's number, the title under it at 72px over two lines, and a line
 * of standfirst ranged right, sitting on the title's last baseline.
 *
 * The title takes its two lines by construction rather than by wrapping, so
 * the break never lands mid-phrase on a long supplier or category name.
 */
interface SectionHeadProps {
    /** Sequence number, shown as "(03)". Omit with `eyebrow` to drop the line. */
    number?: string;
    /** Omit with `number` to drop the eyebrow line entirely. */
    eyebrow?: string;
    /** First line of the title. */
    lead: string;
    /** Second line — the supplier, the category, whatever names this section. */
    subject: string;
    /** Where the subject goes. Unlinked when the section has no destination. */
    subjectLink?: string | null;
    /** Runs as the reader follows `subjectLink`, for click tracking. */
    onSubjectClick?: () => void;
    standfirst?: ReactNode;
    /** The ground it sits on. The project detail page is dark. */
    tone?: 'light' | 'dark';
    /**
     * `editorial` sets the title in the article page's display type —
     * Gestura at the same size — with the subject a step lighter, the way
     * the article closes. The project page's tail speaks in that voice.
     */
    voice?: 'sans' | 'editorial';
    /**
     * Below md, set the subject a step lighter than the lead, in the grey the
     * editorial voice uses — the phone's section heads read as a label and
     * the thing it names.
     */
    mobileMutedSubject?: boolean;
    /** Drop the standfirst below md, leaving the two-line head alone. */
    hideMobileStandfirst?: boolean;
}

export const SectionHead = ({
    number,
    eyebrow,
    lead,
    subject,
    subjectLink,
    onSubjectClick,
    standfirst,
    tone = 'light',
    voice = 'sans',
    mobileMutedSubject = false,
    hideMobileStandfirst = false,
}: SectionHeadProps) => (
    <div className="flex max-md:flex-col max-md:gap-6 md:items-end md:justify-between">
        <div className="flex flex-col max-md:gap-4 md:gap-[2.25rem]">
            {number || eyebrow ? (
                <p
                    className={cn(
                        'm-0 font-sans font-medium uppercase max-md:text-[0.6875rem] max-md:tracking-[0.12em] md:text-[0.75rem] md:tracking-[0.14em]',
                        tone === 'dark' ? 'text-white/50' : 'text-[#6e6a62]',
                    )}
                >
                    {number ? `(${number})` : null}
                    {number && eyebrow ? <>&nbsp;&nbsp;&nbsp;</> : null}
                    {eyebrow}
                </p>
            ) : null}
            <h2
                className={cn(
                    'm-0',
                    voice === 'editorial'
                        ? ARTICLE_DISPLAY_TYPE
                        : SECTION_TITLE_TYPE,
                    // #323232 is the page's ink — the same black the product
                    // title is set in, and the ramp's `colour/ink`. These
                    // heads ran at #121212, a shade found nowhere else, which
                    // read as a second black a few sections down from the
                    // first.
                    tone === 'dark' ? 'text-white' : 'text-[#323232]',
                )}
            >
                {lead}
                <br />
                {subjectLink ? (
                    // The subject is the thing the section is about, so it is
                    // the one part of the head worth clicking. No underline at
                    // this size — the whole line would gain a rule.
                    <Link
                        to={subjectLink}
                        onClick={onSubjectClick}
                        className={cn(
                            'no-underline transition-colors',
                            voice === 'editorial'
                                ? tone === 'dark'
                                    ? 'text-[#a3a3a3] hover:text-white'
                                    : 'text-[#8c8c8c] hover:text-[#323232]'
                                : tone === 'dark'
                                  ? 'text-white hover:text-white/60'
                                  : 'text-[#323232] hover:text-[#6e6a62]',
                            mobileMutedSubject &&
                                (tone === 'dark'
                                    ? 'max-md:text-[#a3a3a3] max-md:hover:text-white'
                                    : 'max-md:text-[#8c8c8c] max-md:hover:text-[#323232]'),
                        )}
                    >
                        {subject}
                    </Link>
                ) : voice === 'editorial' ? (
                    <span
                        className={
                            tone === 'dark'
                                ? 'text-[#a3a3a3]'
                                : 'text-[#8c8c8c]'
                        }
                    >
                        {subject}
                    </span>
                ) : (
                    <span
                        className={cn(
                            mobileMutedSubject &&
                                (tone === 'dark'
                                    ? 'max-md:text-[#a3a3a3]'
                                    : 'max-md:text-[#8c8c8c]'),
                        )}
                    >
                        {subject}
                    </span>
                )}
            </h2>
        </div>

        {standfirst ? (
            <p
                className={cn(
                    'm-0 font-sans leading-[1.6] max-md:text-[0.9375rem] md:w-[21.25rem] md:text-[1.0625rem]',
                    hideMobileStandfirst && 'max-md:hidden',
                    tone === 'dark' ? 'text-white/60' : 'text-[#6e6a62]',
                )}
            >
                {standfirst}
            </p>
        ) : null}
    </div>
);
